import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/lib/supabase";

type IdType = "CC" | "CE" | "NIT" | "PP";

export interface BillingIdentity {
  billing_first_name: string;
  billing_last_name: string;
  billing_id_type: IdType;
  billing_id_number: string;
  billing_phone: string | null;
}

const ID_TYPE_OPTIONS: { value: IdType; label: string }[] = [
  { value: "CC", label: "Cédula de ciudadanía" },
  { value: "CE", label: "Cédula de extranjería" },
  { value: "NIT", label: "NIT" },
  { value: "PP", label: "Pasaporte" },
];

function validateIdNumber(type: IdType, value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return "Ingresa el número de documento";
  if (type === "CC" || type === "CE") {
    if (!/^\d{6,10}$/.test(trimmed)) return "El número debe tener entre 6 y 10 dígitos";
  } else if (type === "NIT") {
    if (!/^\d{9,10}(-\d)?$/.test(trimmed)) return "Formato NIT inválido (ej. 900123456-7)";
  } else if (type === "PP") {
    if (!/^[A-Z0-9]{6,12}$/i.test(trimmed)) return "Formato pasaporte inválido (6-12 caracteres)";
  }
  return null;
}

function validatePhone(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^3\d{9}$/.test(trimmed)) return "Celular COL: 10 dígitos empezando por 3";
  return null;
}

export function BillingIdentityModal({
  open,
  orgId,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  orgId: string;
  initial?: Partial<BillingIdentity> | null;
  onClose: () => void;
  onSaved: (identity: BillingIdentity) => void;
}) {
  const [firstName, setFirstName] = useState(initial?.billing_first_name ?? "");
  const [lastName, setLastName] = useState(initial?.billing_last_name ?? "");
  const [idType, setIdType] = useState<IdType>((initial?.billing_id_type as IdType) ?? "CC");
  const [idNumber, setIdNumber] = useState(initial?.billing_id_number ?? "");
  const [phone, setPhone] = useState(initial?.billing_phone ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst) return toast.error("Ingresa el nombre del titular");
    if (!trimmedLast) return toast.error("Ingresa el apellido del titular");

    const idError = validateIdNumber(idType, idNumber);
    if (idError) return toast.error(idError);

    const phoneError = validatePhone(phone);
    if (phoneError) return toast.error(phoneError);

    setSaving(true);
    try {
      const payload: BillingIdentity = {
        billing_first_name: trimmedFirst,
        billing_last_name: trimmedLast,
        billing_id_type: idType,
        billing_id_number: idNumber.trim(),
        billing_phone: phone.trim() || null,
      };
      const { error, data } = await supabase
        .from("organizations")
        .update(payload)
        .eq("id", orgId)
        .select("id");
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("No se pudo actualizar — recarga la página e intenta de nuevo");
      }
      toast.success("Datos de facturación guardados");
      onSaved(payload);
    } catch (err: any) {
      console.error("[BillingIdentity] save error:", err);
      toast.error(err?.message || "No pudimos guardar los datos");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Datos de facturación</DialogTitle>
          <DialogDescription>
            Los necesitamos una sola vez para procesar el cobro de forma segura en MercadoPago. PSE y Nequi
            requieren documento del titular para autorizar.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="billing-first-name">Nombre</Label>
              <Input
                id="billing-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="María"
                autoComplete="given-name"
                disabled={saving}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="billing-last-name">Apellido</Label>
              <Input
                id="billing-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="González"
                autoComplete="family-name"
                disabled={saving}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label htmlFor="billing-id-type">Tipo doc</Label>
              <Select value={idType} onValueChange={(v) => setIdType(v as IdType)} disabled={saving}>
                <SelectTrigger id="billing-id-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ID_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 col-span-2">
              <Label htmlFor="billing-id-number">Número de documento</Label>
              <Input
                id="billing-id-number"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                placeholder={idType === "NIT" ? "900123456-7" : "1020304050"}
                inputMode={idType === "PP" ? "text" : "numeric"}
                autoComplete="off"
                disabled={saving}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="billing-phone">Celular (opcional)</Label>
            <Input
              id="billing-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="3001234567"
              inputMode="numeric"
              autoComplete="tel"
              disabled={saving}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            {saving ? "Guardando..." : "Guardar y continuar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
